import {
  Action,
  AddRepeatedFieldAction,
  FormState,
  GenericFormData,
  RemoveRepeatedFieldAction,
  SetRepeatedFieldAction,
  SimpleFieldUpdateAction,
  TouchedFields,
  UpdateRepeatedFieldAction,
} from './types.js';

export default function reducer<FormData extends GenericFormData>(
  state: FormState<FormData>,
  action: Action<FormData>,
): FormState<FormData> {
  switch (action.type) {
    case 'simpleUpdate': {
      const {
        payload: { field, value },
      } = action as SimpleFieldUpdateAction<FormData>;
      return {
        formData: {
          ...state.formData,
          [field]: value,
        },
        touchedFields: state.touchedFields[field]
          ? state.touchedFields
          : { ...state.touchedFields, [field]: true },
      };
    }
    case 'addRepeated': {
      const {
        payload: { field, value },
      } = action as AddRepeatedFieldAction<FormData>;
      return {
        ...state,
        formData: {
          ...state.formData,
          [field]: [...state.formData[field], value],
        },
      };
    }
    case 'removeRepeated': {
      const {
        payload: { field, index },
      } = action as RemoveRepeatedFieldAction<FormData>;
      const newArray = [...state.formData[field]];
      newArray.splice(index, 1);
      return {
        ...state,
        formData: { ...state.formData, [field]: newArray },
      };
    }
    case 'setRepeated': {
      const {
        payload: { field, index, value },
      } = action as SetRepeatedFieldAction<FormData>;
      const newArray = [...state.formData[field]];
      newArray[index] = value;
      return {
        ...state,
        formData: { ...state.formData, [field]: newArray },
      };
    }
    case 'updateRepeated': {
      const {
        payload: { index, field, update },
      } = action as UpdateRepeatedFieldAction<FormData>;
      // TODO when a repeated complex field is part of a larger form, intialized in the hook
      // all at once, the touchedField state is a boolean for the complex field. This was causing
      // a serious bug that breaks the hook. We should look further into this and ensure the touched
      // field state represents the array of touched subfields. For now, it's just set to an empty
      // array to prevent issues, but this removes subfield error messaging and proper validation.
      const touchedFields = (
        typeof state.touchedFields[field] === 'boolean' ? [] : state.touchedFields[field] ?? []
      ) as TouchedFields<FormData[typeof field][number]>[];
      const updateResult = reducer(
        {
          formData: state.formData[field][index],
          touchedFields: touchedFields[index] ?? {},
        },
        { type: 'simpleUpdate', payload: update },
      );

      const replace = <T>(arr: T[], value: T): T[] => [
        ...arr.slice(0, index),
        value,
        ...arr.slice(index + 1),
      ];
      return {
        formData: {
          ...state.formData,
          [field]: replace(state.formData[field], updateResult.formData),
        },
        touchedFields:
          updateResult.touchedFields === touchedFields[index]
            ? state.touchedFields
            : {
                ...state.touchedFields,
                [field]: replace(touchedFields, updateResult.touchedFields),
              },
      };
    }
    case 'reset':
      return { formData: action.payload, touchedFields: {} };
    default:
      return state;
  }
}
